import axios, { AxiosPromise } from 'axios';
import ApiService from '@/api/http/ApiService';

/** Report types the reporting service can export. Only the ones used here. */
export enum ReportType {
  FOODSPOT = 'foodspot',
  ORDERS_BY_PAYMENT = 'orders_by_payment',
  VENUE_INVOICE = 'venue_invoice',
  VENUE_INVOICE_SUMMARY = 'venue_invoice_summary',
}

/** Requested output format — some reports force one regardless (invoices: pdf). */
export enum ExportFormat {
  CSV = 'csv',
  XLSX = 'xlsx',
  PDF = 'pdf',
}

/** Lifecycle of an export job. */
export enum ExportStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  DONE = 'done',
  FAILED = 'failed',
}

export interface CreateExportRequest {
  reportType: ReportType;
  format: ExportFormat;
  filters: {
    /** `1d`/`1w`/`1m`/`1y` or an inclusive `YYYY-MM-DD/YYYY-MM-DD` range. */
    period: string;
    /** Single-venue reports read the first entry. */
    venues?: string[];
    customerGroup?: string;
    lang?: string;
    options?: Record<string, unknown>;
  };
}

export interface ExportJob {
  _id: string;
  status: ExportStatus;
  reportType: ReportType;
  format: ExportFormat;
  fileName?: string;
  error?: string;
}

export interface ExportDownload {
  url: string;
  expiresAt: string;
}

/**
 * Asynchronous report exports (reporting service, `v1/reporting/exports`).
 *
 * A job is queued with POST, generated in the background, and fetched through a
 * signed URL once its status is `done` — see DownloadHandler, which polls it.
 */
export default class ReportingExportService extends ApiService<ExportJob> {
  constructor() {
    super('reporting/exports');
  }

  public createExport(payload: CreateExportRequest): AxiosPromise<{ jobId: string; status: ExportStatus }> {
    return axios.post(`${this.getBaseUrl()}`, payload);
  }

  public getExport(id: string): AxiosPromise<ExportJob> {
    return axios.get(`${this.getBaseUrl()}/${id}`);
  }

  /** Signed URL for a finished job; 409 while it is not done yet. */
  public getExportDownloadUrl(id: string): AxiosPromise<ExportDownload> {
    return axios.get(`${this.getBaseUrl()}/${id}/download`);
  }
}
